import * as React from "react";
import { Actions, Config } from "../../context";
import { BULK_SELECT_COLUMN_WIDTH } from "../constants";
import getColumnWidths from "./getColumnWidths";
import { VariableSizeGrid } from "react-window";
import useTable from "../../useTable";

function calcAmountOfColumns(
  columnConfig: Config[],
  hasBulkSelect: boolean,
  actions?: Actions
) {
  let amountOfColumns = columnConfig.length;
  if (hasBulkSelect) {
    amountOfColumns++;
  }
  if (actions) {
    amountOfColumns++;
  }

  return amountOfColumns;
}

function calcColumnWidths(
  width: number,
  columnConfig: Config[],
  hasBulkSelect: boolean,
  actions?: Actions
) {
  let totalRemainingWidth = width;
  if (hasBulkSelect) {
    totalRemainingWidth = totalRemainingWidth - BULK_SELECT_COLUMN_WIDTH;
  }
  if (actions) {
    totalRemainingWidth = totalRemainingWidth - actions.width;
  }

  let columnWidths = getColumnWidths(
    totalRemainingWidth,
    columnConfig.map(x => x.width || {})
  );

  if (hasBulkSelect) {
    columnWidths = [BULK_SELECT_COLUMN_WIDTH, ...columnWidths];
  }
  if (actions) {
    columnWidths = [...columnWidths, actions.width];
  }

  return columnWidths;
}

function useSizing(style: React.CSSProperties = {}) {
  const resizeRef = React.useRef<HTMLDivElement>(null);
  const gridRef = React.useRef<VariableSizeGrid>(null);
  const { columnConfig, hasBulkSelect, actions } = useTable();
  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  const amountOfColumns = calcAmountOfColumns(
    columnConfig,
    hasBulkSelect,
    actions
  );

  const handleResize = React.useCallback(() => {
    const el = resizeRef.current!;

    const width =
      process.env.NODE_ENV === "test"
        ? (style.width as number) || 1400
        : el.offsetWidth;
    const height =
      process.env.NODE_ENV === "test"
        ? (style.height as number) || 600
        : el.offsetHeight;

    setDimensions({ width, height });
  }, []);

  React.useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // force react-window to recalculate column-widths
  React.useLayoutEffect(() => {
    if (gridRef.current) {
      gridRef.current!.resetAfterColumnIndex(0);
    }
  }, [amountOfColumns, dimensions && dimensions.width]);

  const columnWidths = dimensions
    ? calcColumnWidths(dimensions.width, columnConfig, hasBulkSelect, actions)
    : [];

  return {
    dimensions,
    columnWidths,
    resizeRef,
    gridRef,
    amountOfColumns
  };
}

export default useSizing;
